import { AR_CREDIT_NOTE_REASONS, fmt, fmtDate, toNumber } from '@dcc-erp/shared';
import type { ArCreditNote, ArCreditNoteReason } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

// Router-free AR credit-note list. The route owns data fetching
// (useArCreditNotes) + loading/error handling + the engine decision panel; this
// component owns presentation only — a flat table carrying BOTH the accounting
// `status` and the engine `approvalStatus` per row (Design-Z keeps them
// decoupled). A PENDING (engine-managed) row gets a single "ดำเนินการอนุมัติ"
// action that opens the shared 4-eyes panel — exactly as /ap does for
// engine-managed bills. Plain prototype.css classes only.

// The list endpoint enriches each ArCreditNote header with display names the
// wire `ArCreditNote` type does not formally declare (see
// ar-credit-notes.service.listCreditNotes). Narrow them here for the list view.
export type CreditNoteListRow = ArCreditNote & {
  customerName?: string | null;
  invoiceVoucherNo?: string | null;
};

export interface CreditNoteListProps {
  creditNotes: CreditNoteListRow[];
  /** Opens the engine 4-eyes decision panel for a PENDING (engine-managed) CN. */
  onDecide: (cn: CreditNoteListRow) => void;
}

function reasonLabel(code: string): string {
  return AR_CREDIT_NOTE_REASONS[code as ArCreditNoteReason] ?? code;
}

// Accounting-status badge — same vocabulary as the /ap status cell.
function StatusBadge({ status }: { status: ArCreditNote['status'] }) {
  switch (status) {
    case 'POSTED':
      return (
        <span className="badge positive">
          <span className="bdot" />
          ลงบัญชีแล้ว
        </span>
      );
    case 'VOID':
      return (
        <span className="badge negative">
          <span className="bdot" />
          ยกเลิก
        </span>
      );
    case 'PENDING':
      return (
        <span className="badge warning">
          <span className="bdot" />
          รออนุมัติ
        </span>
      );
    case 'RETURNED':
      return (
        <span className="badge warning">
          <span className="bdot" />
          ตีกลับแก้ไข
        </span>
      );
    default:
      return (
        <span className="badge info">
          <span className="bdot" />
          ร่าง
        </span>
      );
  }
}

export function CreditNoteList({ creditNotes, onDecide }: CreditNoteListProps) {
  return (
    <div className="tbl-shell">
      <div className="tbl-wrap" style={{ maxHeight: 600 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>เลขที่ใบลดหนี้</th>
              <th>วันที่</th>
              <th>ลูกค้า</th>
              <th>ใบแจ้งหนี้อ้างอิง</th>
              <th>เหตุผล</th>
              <th className="num">มูลค่ารวม</th>
              <th>สถานะบัญชี</th>
              <th>สายอนุมัติ</th>
            </tr>
          </thead>
          <tbody>
            {creditNotes.length === 0 ? (
              <tr>
                <td colSpan={8} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  ไม่มีข้อมูล
                </td>
              </tr>
            ) : (
              creditNotes.map((cn) => {
                // A PENDING credit note sitting in the engine (approvalStatus set)
                // exposes the central 4-eyes decision action — identical gate to
                // the /ap engine-managed row.
                const inEngine = cn.approvalStatus != null && cn.status === 'PENDING';
                return (
                  <tr key={cn.id}>
                    <td>
                      <span className="code">{cn.voucherNo}</span>
                    </td>
                    <td>{fmtDate(cn.creditNoteDate)}</td>
                    <td>{cn.customerName ?? cn.customerId}</td>
                    <td className="muted">{cn.invoiceVoucherNo ?? '—'}</td>
                    <td className="muted">{reasonLabel(cn.reasonCode)}</td>
                    <td className="num tnum">{fmt(toNumber(cn.totalAmount), 2)}</td>
                    <td>
                      <StatusBadge status={cn.status} />
                    </td>
                    <td>
                      {inEngine ? (
                        <button
                          className="btn primary"
                          style={{ padding: '2px 8px', fontSize: 12 }}
                          onClick={() => onDecide(cn)}
                          title="ดำเนินการอนุมัติ 4 ตา (สายอนุมัติกลาง)"
                        >
                          <Icon name="check" size={12} /> ดำเนินการอนุมัติ
                        </button>
                      ) : cn.approvalStatus ? (
                        <span className="muted" style={{ fontSize: 12 }}>
                          {cn.approvalStatus}
                        </span>
                      ) : (
                        <span className="muted" style={{ fontSize: 12 }}>
                          —
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
